import type { ValidationError } from '../types';
import { constSelector, useRecoilValue } from 'recoil';
import {
  blockLimitedRequestSubjects,
  blockMaxSubjects,
  blockRequestSubjects,
  blockSettings,
  blockSubjectInputGroups,
  customizedBlock,
  isProFormaBlockOrMetric,
  subjectInputGroupSubjects,
} from 'venn-state';
import {
  type CustomBlockTypeEnum,
  hasDisabledSubjectInputs,
  isFactorTrendMetric,
  isHistoricalSubject,
  isPublicPrivateAssetGrowthBlock,
  plural,
  PRO_FORMA_HISTORICAL_AS_OF_DATE_PARAMETERIZED_BLOCKS,
  PRO_FORMA_HISTORICAL_SIMULATION_DATE_BLOCKS,
} from 'venn-utils';

const getHistoricalProFormaSubjectsMessage = (blockType: CustomBlockTypeEnum) => {
  // Blocks with explicit as of date selector don't need a notice since it's clear what allocations are being used
  if (PRO_FORMA_HISTORICAL_AS_OF_DATE_PARAMETERIZED_BLOCKS.includes(blockType)) {
    return null;
  }

  if (PRO_FORMA_HISTORICAL_SIMULATION_DATE_BLOCKS.includes(blockType)) {
    return 'Portfolios with historical allocations on this block use latest uploaded allocations, adjusted for market drift between allocation date and simulation start date.';
  }

  if (blockType === 'CORRELATION') {
    return 'Portfolios with historical allocations on this block use latest uploaded allocations.';
  }

  if (blockType === 'SCENARIO') {
    return 'Portfolios with historical allocations on this block use latest uploaded allocations, adjusted for market drift between allocation date and forecast start date.';
  }

  return null;
};

const emptySelector = constSelector([]);

export const useBlockSubjectValidation = (selectedBlockId: string): ValidationError[] => {
  const blockSubjectGroups = useRecoilValue(blockSubjectInputGroups(selectedBlockId));
  const subjects = useRecoilValue(blockRequestSubjects(selectedBlockId));
  const limitedSubjects = useRecoilValue(blockLimitedRequestSubjects(selectedBlockId));
  const maxSubjects = useRecoilValue(blockMaxSubjects(selectedBlockId));
  const blockType = useRecoilValue(blockSettings(selectedBlockId)).customBlockType;
  const selectedBlock = useRecoilValue(customizedBlock(selectedBlockId));
  const isProForma = useRecoilValue(isProFormaBlockOrMetric(selectedBlockId));
  const firstGroup = blockSubjectGroups[0];
  const firstGroupSubjects = useRecoilValue(firstGroup ? subjectInputGroupSubjects(firstGroup) : emptySelector);

  const errors: ValidationError[] = [];

  if (!hasDisabledSubjectInputs(blockType) && blockSubjectGroups.length === 0) {
    errors.push({
      message: 'No subject groups are assigned to this block.',
      type: 'ERROR',
    });
  }

  if (isPublicPrivateAssetGrowthBlock(blockType) && limitedSubjects.length < 2) {
    errors.push({
      message: 'Your Subject Group must include one Returns-Based subject and one Private Asset subject.',
      type: 'ERROR',
    });
  }

  if (limitedSubjects.some(isHistoricalSubject) && isProForma) {
    const message = getHistoricalProFormaSubjectsMessage(blockType);
    message &&
      errors.push({
        message,
        type: 'INFO',
      });
  }

  const isFactorTrendLineChart =
    selectedBlock.infoGraphicType === 'LINE' && isFactorTrendMetric(selectedBlock.selectedMetrics);
  if (isFactorTrendLineChart && (blockSubjectGroups.length > 1 || firstGroupSubjects.length > 1)) {
    errors.push({
      message: 'For best results select a single factor or a single investment/portfolio.',
      type: 'WARNING',
    });
  }

  if (subjects.length > maxSubjects) {
    const message = isPublicPrivateAssetGrowthBlock(blockType)
      ? 'This block accommodates a maximum of 1 Returns-Based subject and 1 Private Asset subject'
      : `This block accommodates a max of ${maxSubjects} ${plural(maxSubjects, {
          1: 'subject',
          other: 'subjects',
        })}`;
    message &&
      errors.push({
        message,
        type: 'WARNING',
      });
  }

  return errors;
};
